import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useLocation, useUpdateLocation } from '../api';
import { locationFormSchema, type LocationFormInput } from '../schema';
import { LocationForm } from './LocationForm';

export function UpdateLocationPage() {
  const navigate = useNavigate();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = Number(locationIdParam);
  const detail = useLocation(locationId);
  const update = useUpdateLocation();

  const form = useForm<LocationFormInput>({
    resolver: zodResolver(locationFormSchema),
    mode: 'onTouched',
    defaultValues: { locationName: '' },
  });

  useEffect(() => {
    if (!detail.data) return;
    form.reset({ locationName: detail.data.locationName ?? '' });
  }, [detail.data, form]);

  const onSubmit = (input: LocationFormInput) => {
    update.mutate(
      { locationId, locationName: input.locationName.trim() },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Location updated');
          navigate('/location-list');
        },
        onError: (err) =>
          toast.error(readErrorMessage(err) ?? 'Could not update the location.'),
      },
    );
  };

  return (
    <LocationForm
      mode="update"
      form={form}
      onSubmit={onSubmit}
      isPending={update.isPending}
      pageTitle="Update Location"
      pageDescription={
        detail.data ? `Editing "${detail.data.locationName}".` : 'Loading location details…'
      }
    />
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
